import { Link } from 'react-router-dom'
import type { PersonalLoan } from '@/api/personalLoans'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { formatShortDate } from '@/lib/formatDate'
import { formatInterest } from '@/lib/personalLoans'
import { ROUTES } from '@/routes/routes'
import { LoanStatusBadges } from './LoanStatusBadges'

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')
}

export function PersonalLoanRow({
  loan,
  title,
  avatar = false,
}: {
  loan: PersonalLoan
  title: string
  /** Show the person's initials, for lists where rows are people rather than loans. */
  avatar?: boolean
}) {
  const format = useListAmountFormat()
  return (
    <li>
      <Link
        to={ROUTES.APP.PERSONAL_LOAN_DETAIL(loan.accountId)}
        className={`flex items-center gap-3 px-4 py-3 hover:bg-[var(--fl-surface-hover)] ${
          loan.closed ? 'opacity-60' : ''
        }`}
      >
        {avatar && (
          <span
            aria-hidden="true"
            className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-[var(--fl-primary-faint)] text-[13px] font-semibold text-[var(--fl-primary)]"
          >
            {initials(title)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-[var(--fl-text-1)]">{title}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[var(--fl-text-2)]">
            <span>{formatInterest(loan.interestBasis, loan.interestRate)}</span>
            {loan.dueDate && <span>Due {formatShortDate(loan.dueDate)}</span>}
            <LoanStatusBadges loan={loan} />
          </p>
        </div>
        <span className="shrink-0 text-right">
          <span
            className="block text-sm font-semibold tabular-nums"
            style={{ color: loan.direction === 'LENT' ? 'var(--fl-income)' : 'var(--fl-error)' }}
          >
            {format(loan.outstanding)}
          </span>
          {avatar && (
            <span className="block text-[11.5px] text-[var(--fl-text-3)]">
              {loan.principalRepaid > 0
                ? `${format(loan.principalRepaid)} repaid`
                : 'nothing repaid yet'}
            </span>
          )}
        </span>
      </Link>
    </li>
  )
}
